import { useState } from "react";
import { ExternalLink, Mail } from "lucide-react";

import { Button } from "@/design-system";

import { useMicrosoft365ConnectMutation } from "./hooks";
import { redirectToExternalUrl } from "./navigation";


const CONNECT_ERROR =
  "Unable to start Microsoft 365 connection. Try again.";


export function IntegrationsTab() {
  const connectMicrosoft365 = useMicrosoft365ConnectMutation();
  const [error, setError] = useState<string | null>(null);

  const busy = connectMicrosoft365.isPending;
  const statusText = busy
    ? "Opening Microsoft authorization..."
    : "Connect BeneCRM to Microsoft 365 email and calendar.";

  const onConnect = async () => {
    setError(null);
    try {
      const response = await connectMicrosoft365.mutateAsync();
      if (!response.authorize_url) {
        throw new Error("missing_authorize_url");
      }
      redirectToExternalUrl(response.authorize_url);
    } catch {
      setError(CONNECT_ERROR);
    }
  };

  return (
    <div
      data-testid="settings-integrations-tab"
      className="flex flex-col gap-5"
    >
      <section className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-raised p-4">
        <header className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold text-text-primary">
            Microsoft 365
          </h2>
          <p className="max-w-2xl text-xs text-text-secondary">
            Authorize BeneCRM to connect email and calendar activity for your
            account.
          </p>
        </header>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p
            id="settings-microsoft-connect-status"
            data-testid="settings-microsoft-connect-status"
            className="text-sm text-text-secondary"
            aria-live="polite"
          >
            {statusText}
          </p>
          <Button
            variant="primary"
            size="sm"
            loading={busy}
            disabled={busy}
            aria-label={
              busy
                ? "Opening Microsoft authorization"
                : "Connect Microsoft 365"
            }
            aria-describedby={
              error
                ? "settings-microsoft-connect-error"
                : "settings-microsoft-connect-status"
            }
            data-testid="settings-microsoft-connect-button"
            className="min-w-48"
            onClick={onConnect}
          >
            <Mail size={16} aria-hidden="true" />
            <span>Connect Microsoft 365</span>
            <ExternalLink size={14} aria-hidden="true" />
          </Button>
        </div>

        {error && (
          <p
            id="settings-microsoft-connect-error"
            role="alert"
            data-testid="settings-microsoft-connect-error"
            className="rounded-md border border-ember/20 bg-ember/10 px-3 py-2 text-sm text-ember-ink"
          >
            {error}
          </p>
        )}
      </section>
    </div>
  );
}
